import React, { useEffect, useState } from 'react';
import { Form } from '@/components/ui';
import { vehicleTransferService } from '../../../services/vehicleTransferService';

export interface PickedPerson {
  id: number;
  name: string;
}

interface Props {
  id: string;
  value: PickedPerson | null;
  onChange: (person: PickedPerson | null) => void;
}

/** Type a name, pick an active user. Admin-only screens (settings, reassign). */
const TransferPersonPicker: React.FC<Props> = ({ id, value, onChange }) => {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<{ id: number; name: string; role: string | null }[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (search.trim().length < 2) {
      setResults([]);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      vehicleTransferService
        .people(search)
        .then((people) => !cancelled && (setResults(people), setError(null)))
        .catch((err) => !cancelled && setError(err instanceof Error ? err.message : 'Search failed'));
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [search]);

  return (
    <div>
      {value && (
        <div className="tw-mb-2 tw-flex tw-items-center tw-justify-between tw-gap-2 tw-rounded tw-border tw-border-line tw-bg-surface-alt tw-px-3 tw-py-2 tw-text-sm">
          <span className="tw-font-medium">{value.name}</span>
          <button type="button" className="tw-text-xs tw-text-primary hover:tw-underline" onClick={() => onChange(null)}>
            Change
          </button>
        </div>
      )}
      {!value && (
        <>
          <Form.Control id={id} placeholder="Type at least 2 letters of a name" value={search} onChange={(e) => setSearch(e.target.value)} autoComplete="off" />
          {error && <p className="tw-mb-0 tw-mt-1 tw-text-sm tw-text-danger">{error}</p>}
          {results.length > 0 && (
            <ul className="tw-mt-1 tw-max-h-56 tw-overflow-y-auto tw-rounded tw-border tw-border-line tw-pl-0" role="listbox" aria-label="Matching people">
              {results.map((p) => (
                <li key={p.id} className="tw-list-none">
                  <button
                    type="button"
                    role="option"
                    aria-selected={false}
                    className="tw-flex tw-w-full tw-justify-between tw-gap-2 tw-px-3 tw-py-2 tw-text-left tw-text-sm hover:tw-bg-surface-alt"
                    onClick={() => {
                      onChange({ id: p.id, name: p.name });
                      setSearch('');
                      setResults([]);
                    }}
                  >
                    <span>{p.name}</span>
                    <span className="tw-text-xs tw-text-content-muted">{p.role ?? ''}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {search.trim().length >= 2 && results.length === 0 && !error && (
            <p className="tw-mb-0 tw-mt-1 tw-text-sm tw-text-content-muted">No active user matches.</p>
          )}
        </>
      )}
    </div>
  );
};

export default TransferPersonPicker;
